<template>
  <el-container class="layout-container">
    <!-- Sidebar -->
    <el-aside :width="isCollapse ? '64px' : '240px'" class="layout-aside">
      <div class="logo-bar">
        <div class="logo-icon">🌾</div>
        <div v-show="!isCollapse" class="logo-text">
          <div class="logo-title">工程北米</div>
          <div class="logo-sub">数字有机农场追溯系统</div>
        </div>
      </div>

      <el-menu
        :default-active="activeMenu"
        :collapse="isCollapse"
        :collapse-transition="false"
        class="aside-menu"
        router
      >
        <!-- 1. 数据大屏 -->
        <el-menu-item v-if="hasPermission('dashboard')" index="/dashboard">
          <el-icon><DataLine /></el-icon>
          <template #title>数据大屏</template>
        </el-menu-item>

        <!-- 2. 地块管理 -->
        <el-sub-menu v-if="hasModule('land')" index="land">
          <template #title>
            <el-icon><Location /></el-icon>
            <span>地块管理</span>
          </template>
          <el-menu-item v-if="hasPermission('land:bases')" index="/land/bases">种植基地管理</el-menu-item>
          <el-menu-item v-if="hasPermission('land:list')" index="/land/list">地块档案列表</el-menu-item>
          <el-menu-item v-if="hasPermission('land:varieties')" index="/land/varieties">水稻品种管理</el-menu-item>
          <el-menu-item v-if="hasPermission('land:map')" index="/land/map">基地电子地图</el-menu-item>
          <el-menu-item v-if="hasPermission('land:certificates')" index="/land/certificates">有机资质归档</el-menu-item>
          <el-menu-item v-if="hasPermission('land:ledgers')" index="/land/ledgers">有机台账归档</el-menu-item>
        </el-sub-menu>

        <!-- 3. 种植农事追溯 -->
        <el-sub-menu v-if="hasModule('farming')" index="farming">
          <template #title>
            <el-icon><Calendar /></el-icon>
            <span>种植农事追溯</span>
          </template>
          <el-menu-item v-if="hasPermission('farming:records')" index="/farming/records">农事台账记录</el-menu-item>
          <el-menu-item v-if="hasPermission('farming:pesticides')" index="/farming/pesticides">每亩用药明细</el-menu-item>
          <el-menu-item v-if="hasPermission('farming:harvests')" index="/farming/harvests">测产与收获管理</el-menu-item>
          <el-menu-item v-if="hasPermission('farming:videos')" index="/farming/videos">农场视频监控</el-menu-item>
        </el-sub-menu>

        <!-- 4. 生产加工追溯 -->
        <el-sub-menu v-if="hasModule('processing')" index="processing">
          <template #title>
            <el-icon><OfficeBuilding /></el-icon>
            <span>生产加工追溯</span>
          </template>
          <el-menu-item v-if="hasPermission('processing:orders')" index="/processing/orders">生产订单管理</el-menu-item>
          <el-menu-item v-if="hasPermission('processing:production-tasks')" index="/processing/production-tasks">MES 生产任务</el-menu-item>
          <el-menu-item v-if="hasPermission('processing:processing-tasks')" index="/processing/processing-tasks">MES 加工任务</el-menu-item>
        </el-sub-menu>

        <!-- 5. 溯源码管理 -->
        <el-sub-menu v-if="hasModule('tracecode')" index="tracecode">
          <template #title>
            <el-icon><Discount /></el-icon>
            <span>溯源码管理</span>
          </template>
          <el-menu-item v-if="hasPermission('tracecode:stats')" index="/tracecode/stats">溯源和核验统计</el-menu-item>
          <el-menu-item v-if="authStore.isSuperAdmin || hasPermission('tracecode:enterprises')" index="/tracecode/enterprises">企业赋码管理</el-menu-item>
          <el-menu-item v-if="hasPermission('tracecode:products')" index="/tracecode/products">品类赋码管理</el-menu-item>
          <el-menu-item v-if="hasPermission('tracecode:generate')" index="/tracecode/generate">一物一码批量生成</el-menu-item>
          <el-menu-item v-if="hasPermission('tracecode:packages')" index="/tracecode/packages">二维码印刷导出</el-menu-item>
        </el-sub-menu>

        <!-- 6. 系统管理 (仅管理员或具备权限角色可见) -->
        <el-sub-menu v-if="hasModule('system')" index="system">
          <template #title>
            <el-icon><Setting /></el-icon>
            <span>系统与权限</span>
          </template>
          <el-menu-item v-if="hasPermission('system:users')" index="/system/users">用户管理</el-menu-item>
          <el-menu-item v-if="hasPermission('system:roles')" index="/system/roles">角色管理</el-menu-item>
          <el-menu-item v-if="hasPermission('system:logs')" index="/system/logs">日志审计</el-menu-item>
          <el-menu-item v-if="hasPermission('system:notarizations')" index="/system/notarizations">公证文件管理</el-menu-item>
        </el-sub-menu>
      </el-menu>
    </el-aside>

    <!-- Main Content Area -->
    <el-container class="layout-main-wrapper">
      <!-- Header -->
      <el-header class="layout-header">
        <div class="header-left">
          <el-button
            link
            class="collapse-btn"
            @click="isCollapse = !isCollapse"
          >
            <el-icon :size="20">
              <Fold v-if="!isCollapse" />
              <Expand v-else />
            </el-icon>
          </el-button>

          <el-breadcrumb separator="/" class="breadcrumb">
            <el-breadcrumb-item :to="{ path: '/dashboard' }">首页</el-breadcrumb-item>
            <el-breadcrumb-item>{{ currentRouteTitle }}</el-breadcrumb-item>
          </el-breadcrumb>
        </div>

        <div class="header-right">
          <!-- 超管企业穿透视角切换器 -->
          <div v-if="authStore.isSuperAdmin" class="tenant-switcher">
            <span class="switcher-label">🏢 企业穿透视角:</span>
            <el-select
              v-model="selectedTenantEnterprise"
              size="small"
              class="tenant-select"
              @change="handleTenantChange"
            >
              <el-option label="🌐 全部代工企业 (全局数据穿透)" value="all" />
              <el-option label="🌾 五常市龙凤山水稻合作社 (自营)" :value="8001" />
              <el-option label="🏭 嫩江市金穗生态农业深加工" :value="8002" />
              <el-option label="👶 秋田满满 (杭州) 电子商务有限公司" :value="8003" />
              <el-option label="🍼 英氏 (Engnice) 营养食品科技有限公司" :value="8004" />
            </el-select>
          </div>
          <div v-else class="tenant-badge">
            <el-tag type="info" effect="plain" class="ent-tag">
              🏢 归属企业: {{ authStore.user?.tenantName || '本企业数据隔离' }}
            </el-tag>
          </div>

          <el-tag type="success" effect="light" class="iot-tag">
            <span class="pulse-dot"></span>
            北斗 IoT 4/4 在线
          </el-tag>

          <el-dropdown trigger="click" @command="handleCommand">
            <div class="user-profile">
              <el-avatar :size="32" :src="authStore.user?.avatar" />
              <div class="user-info">
                <span class="user-name">{{ authStore.userName }}</span>
                <span class="user-role">{{ authStore.userRoleName }}</span>
              </div>
              <el-icon class="dropdown-arrow"><ArrowDown /></el-icon>
            </div>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item disabled>
                  <div style="font-size: 12px; color: #64748B;">
                    账号: {{ authStore.user?.username }} ({{ authStore.userRoleName }})
                  </div>
                </el-dropdown-item>
                <el-dropdown-item divided command="changePassword">
                  <span style="display: inline-flex; align-items: center; gap: 6px; color: #334155;">
                    <el-icon><Lock /></el-icon>
                    <span>修改登录密码</span>
                  </span>
                </el-dropdown-item>
                <el-dropdown-item command="logout">
                  <span style="color: #DC2626; display: inline-flex; align-items: center; gap: 6px;">
                    <el-icon><SwitchButton /></el-icon>
                    <span>退出登录</span>
                  </span>
                </el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </div>
      </el-header>

      <!-- Main Router Body -->
      <el-main class="layout-body">
        <router-view v-slot="{ Component, route }">
          <transition name="fade" mode="out-in">
            <component :is="Component" :key="route.path + '_' + (authStore.filterEnterpriseId || '')" />
          </transition>
        </router-view>
      </el-main>
    </el-container>

    <!-- 个人中心：修改登录密码弹窗 -->
    <el-dialog
      v-model="pwdDialogVisible"
      title="修改登录密码"
      width="460px"
      destroy-on-close
      append-to-body
      :close-on-click-modal="false"
    >
      <div style="margin-bottom: 16px; padding: 10px 14px; background: #F8FAFC; border-radius: 8px; border: 1px solid #E2E8F0; font-size: 13px; color: #475569;">
        <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
          <span>当前用户：<strong>{{ authStore.userName }}</strong></span>
          <span style="color: #15803D; font-weight: 600;">{{ authStore.userRoleName }}</span>
        </div>
        <div style="font-size: 12px; color: #64748B;">
          登录账号：<code>{{ authStore.user?.username }}</code>
        </div>
      </div>

      <el-form
        ref="pwdFormRef"
        :model="pwdForm"
        :rules="pwdRules"
        label-width="96px"
        label-position="right"
      >
        <el-form-item label="原密码" prop="oldPassword">
          <el-input
            v-model="pwdForm.oldPassword"
            type="password"
            placeholder="请输入当前账号的原密码"
            show-password
            autocomplete="current-password"
          />
        </el-form-item>
        <el-form-item label="新密码" prop="newPassword">
          <el-input
            v-model="pwdForm.newPassword"
            type="password"
            placeholder="6-32位，建议含字母与数字"
            show-password
            autocomplete="new-password"
          />
        </el-form-item>
        <el-form-item label="确认新密码" prop="confirmPassword">
          <el-input
            v-model="pwdForm.confirmPassword"
            type="password"
            placeholder="请再次输入新密码"
            show-password
            autocomplete="new-password"
          />
        </el-form-item>
      </el-form>

      <template #footer>
        <span class="dialog-footer">
          <el-button :disabled="pwdSubmitting" @click="pwdDialogVisible = false">取消</el-button>
          <el-button type="primary" :loading="pwdSubmitting" @click="handleConfirmChangePassword">
            确认修改并重新登录
          </el-button>
        </span>
      </template>
    </el-dialog>
  </el-container>
</template>

<script setup lang="ts">
import { ref, reactive, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { ElMessage, ElMessageBox, type FormInstance, type FormRules } from 'element-plus';
import { useAuthStore } from '../store/useAuthStore';
import { post } from '../api/request';

const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();

const isCollapse = ref(false);
const selectedTenantEnterprise = ref<number | 'all'>(authStore.filterEnterpriseId || 'all');

const activeMenu = computed(() => route.path);
const currentRouteTitle = computed(() => (route.meta?.title as string) || '页面');

// 模块级别 RBAC 权限过滤
const hasModule = (module: string) => {
  if (authStore.permissions.includes('*')) return true;
  if (authStore.permissions.includes(module)) return true;
  return authStore.permissions.some((p) => p.startsWith(`${module}:`));
};

// 菜单与操作级别细粒度 RBAC 权限过滤
const hasPermission = (code: string) => {
  return authStore.hasPermission(code);
};

const handleTenantChange = (val: number | 'all') => {
  authStore.setFilterEnterpriseId(val);
  if (val === 'all') {
    ElMessage.success('已切换至 [全部企业 (全局数据模型穿透)] 视角');
  } else {
    ElMessage.success('已切换至对应代工企业数据视角');
  }
};

// 自助修改密码相关状态与校验逻辑
const pwdDialogVisible = ref(false);
const pwdSubmitting = ref(false);
const pwdFormRef = ref<FormInstance>();
const pwdForm = reactive({
  oldPassword: '',
  newPassword: '',
  confirmPassword: '',
});

const pwdRules = reactive<FormRules>({
  oldPassword: [
    { required: true, message: '请输入当前账号的原密码', trigger: 'blur' },
  ],
  newPassword: [
    { required: true, message: '请输入新密码', trigger: 'blur' },
    { min: 6, max: 32, message: '密码长度需在 6 至 32 个字符之间', trigger: 'blur' },
    {
      validator: (_rule, value, callback) => {
        if (value && value === pwdForm.oldPassword) {
          callback(new Error('新密码不能与原密码相同'));
        } else {
          callback();
        }
      },
      trigger: 'blur',
    },
  ],
  confirmPassword: [
    { required: true, message: '请确认新密码', trigger: 'blur' },
    {
      validator: (_rule, value, callback) => {
        if (value !== pwdForm.newPassword) {
          callback(new Error('两次输入的新密码不一致'));
        } else {
          callback();
        }
      },
      trigger: 'blur',
    },
  ],
});

const handleConfirmChangePassword = async () => {
  if (!pwdFormRef.value) return;
  await pwdFormRef.value.validate(async (valid) => {
    if (!valid) return;
    pwdSubmitting.value = true;
    try {
      await post('/auth/change-password', {
        oldPassword: pwdForm.oldPassword,
        newPassword: pwdForm.newPassword,
        username: authStore.user?.username,
      });
      pwdDialogVisible.value = false;
      ElMessageBox.alert('密码修改成功，请使用新密码重新登录！', '修改成功', {
        confirmButtonText: '确定',
        type: 'success',
      }).then(() => {
        authStore.logout();
        router.push('/login');
      });
    } catch (err: any) {
      ElMessage.error(err?.message || '密码修改失败，请检查原密码是否正确');
    } finally {
      pwdSubmitting.value = false;
    }
  });
};

const handleCommand = async (cmd: string) => {
  if (cmd === 'logout') {
    ElMessageBox.confirm('确认退出当前账号登录状态？', '提示', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning',
    }).then(() => {
      authStore.logout();
      ElMessage.success('已安全退出登录');
      router.push('/login');
    });
  } else if (cmd === 'changePassword') {
    pwdForm.oldPassword = '';
    pwdForm.newPassword = '';
    pwdForm.confirmPassword = '';
    pwdDialogVisible.value = true;
  }
};
</script>

<style scoped>
.layout-container {
  min-height: 100vh;
  background-color: #F8FAFC;
}

.layout-aside {
  background-color: #FFFFFF;
  color: #1E293B;
  display: flex;
  flex-direction: column;
  transition: width 0.25s ease;
  border-right: 1px solid #E2E8F0;
  box-shadow: 1px 0 6px rgba(0, 0, 0, 0.02);
}

.logo-bar {
  height: 60px;
  display: flex;
  align-items: center;
  padding: 0 16px;
  background-color: #FFFFFF;
  border-bottom: 1px solid #E2E8F0;
  gap: 12px;
}

.logo-icon {
  font-size: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.logo-text {
  overflow: hidden;
  white-space: nowrap;
}

.logo-title {
  font-size: 15px;
  font-weight: 800;
  color: #15803D;
  letter-spacing: 0.5px;
}

.logo-sub {
  font-size: 11px;
  color: #64748B;
}

.aside-menu {
  border-right: none;
  flex: 1;
  overflow-y: auto;
}

.layout-main-wrapper {
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: hidden;
}

.layout-header {
  height: 60px;
  background-color: #FFFFFF;
  border-bottom: 1px solid #E2E8F0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
}

.header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.collapse-btn {
  color: #64748B;
  padding: 4px;
}

.breadcrumb {
  font-size: 13px;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 16px;
}

.tenant-switcher {
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: #F1F5F9;
  padding: 4px 12px;
  border-radius: 8px;
  border: 1px solid #E2E8F0;
}

.switcher-label {
  font-size: 12px;
  font-weight: 700;
  color: #334155;
  white-space: nowrap;
}

.tenant-select {
  width: 250px;
}

.ent-tag {
  font-weight: 600;
}

.iot-tag {
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 600;
  border-radius: 20px;
  padding: 4px 12px;
}

.pulse-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: #15803D;
  display: inline-block;
  box-shadow: 0 0 0 0 rgba(21, 128, 61, 0.7);
  animation: pulse 1.8s infinite cubic-bezier(0.66, 0, 0, 1);
}

@keyframes pulse {
  to {
    box-shadow: 0 0 0 8px rgba(21, 128, 61, 0);
  }
}

.user-profile {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 8px;
  transition: background-color 0.2s;
}

.user-profile:hover {
  background-color: #F1F5F9;
}

.user-info {
  display: flex;
  flex-direction: column;
  text-align: left;
}

.user-name {
  font-size: 13px;
  font-weight: 700;
  color: #0F172A;
  line-height: 1.2;
}

.user-role {
  font-size: 11px;
  color: #64748B;
  line-height: 1.2;
}

.dropdown-arrow {
  font-size: 12px;
  color: #94A3B8;
}

.layout-body {
  flex: 1;
  padding: 20px 24px;
  overflow-y: auto;
  background-color: #F8FAFC;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
